import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import {
	assetsOpened,
	classificationLoadSuccess,
	contactsOpened,
	fileOpened,
	getLayoutOptions,
	incidentsOpened,
	loadedNsFieldsKeyValues,
	messagesOpened,
	requestOpened,
	selectLayoutType,
	workOrdersOpened,
} from '@app/pages/cmms/state/cmms.actions';
import {
	IWorkOrderState,
	workOrderReducer,
} from '@app/pages/cmms/components/work-order/state/work-order.reducer';
import {
	IRequestState,
	requestReducer,
} from '@app/pages/cmms/components/request/state/requests.reducer';
import {
	IIncidentState,
	incidentReducer,
} from '@app/pages/cmms/components/incident/state/incidents.reducer';
import {
	fileReducer,
	IFileState,
} from '@app/pages/cmms/components/files/state/files.reducer';
import {
	assetReducer,
	IIfcAssetsState,
} from '@app/pages/cmms/components/assets/state/assets.reducer';
import { CMMS_STATUSES } from '@app/pages/cmms/configs/statuses-by-entity';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import {
	CMMSLayoutType,
	ILayoutOptions,
} from '@app/pages/cmms/interfaces/layout.interfaces';
import { CMMS_LAYOUT_OPTIONS } from '@app/pages/cmms/configs/layout-options.config';
import { MaintenanceSelectionTypes } from '@app/pages/cmms/configs/cmms-maintenance.enums';
import {
	IMessagesState,
	messagesReducer,
} from '@app/pages/messages/state/messages.reducer';
import { contactsReducer } from '@app/pages/contacts/state/contacts.reducer';
import { IContacts } from '@app/pages/contacts/interfaces/contact.interfaces';

export const CmmsFeatureKey = 'cmms';

export enum CmmsPages {
	// TODO Rollback to normal active page, that is current active main table
	wo = 'work_orders',
	newWorkOrder = 'New Work Order',
	editWorkOrder = 'Edit Work Order',
	incidents = 'Incidents',
	newIncident = 'New Incidents',
	editIncident = 'Edit Incident',
	requests = 'Requests',
	newRequest = 'New Requests',
	newAsset = 'New Asset',
	editRequest = 'Edit Requests',
	existingEntities = 'existingEntities',
	editFile = 'File Edit',
	files = 'Files',
	assets = 'Assets',
	editAsset = 'Edit Assets',
	fileDetector = 'File Detector',
	fileImport = 'File Import',
	// TODO 100% only in workspace components
	attachmentWorkspace = 'Attachment Workspace',
	messages = 'Messages',
	viewMessage = 'View Message',
	contacts = 'Contacts',
}

export interface ICmmsState {
	activePage: CmmsPages | null;
	classifications: IClassification[] | null;
	statuses: {
		[CmmsPages.wo]: string[];
		[CmmsPages.incidents]: string[];
		[CmmsPages.requests]: string[];
	};
	layoutOptions: ILayoutOptions[];
	selectedLayoutType: CMMSLayoutType;
	nsFieldsData: {
		nsFieldsKeyValues?: {
			[key in MaintenanceSelectionTypes]: {
				enumValues: { name: string; description: string }[];
				label: string;
			};
		};
		nameDescriptionList?: { [name: string]: string };
	};
}

export const initialCMMSState: ICmmsState = {
	activePage: null,
	classifications: [],
	statuses: {
		[CmmsPages.wo]: CMMS_STATUSES.WORK_ORDERS,
		[CmmsPages.incidents]: CMMS_STATUSES.INCIDENTS,
		[CmmsPages.requests]: CMMS_STATUSES.REQUESTS,
	},
	layoutOptions: CMMS_LAYOUT_OPTIONS(),
	selectedLayoutType: 'table',
	nsFieldsData: {
		nsFieldsKeyValues: null,
		nameDescriptionList: {},
	},
};

export const cmmsReducer = createReducer(
	initialCMMSState,
	on(workOrdersOpened, state => ({
		...state,
		layoutOptions: CMMS_LAYOUT_OPTIONS(false, true), //remove 'true' when list view is ready for work orders. (Can probably remove both args then, first arg is calendar view.)
		activePage: CmmsPages.wo,
	})),
	on(incidentsOpened, state => ({
		...state,
		layoutOptions: CMMS_LAYOUT_OPTIONS(false, true), //remove 'true' when list view is ready for incidents.  (Can probably remove both args then, first arg is calendar view.)
		activePage: CmmsPages.incidents,
	})),
	on(requestOpened, state => ({
		...state,
		activePage: CmmsPages.requests,
	})),
	on(fileOpened, state => ({
		...state,
		activePage: CmmsPages.files,
	})),
	on(assetsOpened, state => ({
		...state,
		layoutOptions: CMMS_LAYOUT_OPTIONS(true),
		activePage: CmmsPages.assets,
	})),

	on(messagesOpened, state => ({
		...state,
		activePage: CmmsPages.messages,
	})),

	on(contactsOpened, state => ({
		...state,
		activePage: CmmsPages.contacts,
	})),

	on(classificationLoadSuccess, (state, { classifications }) => ({
		...state,
		classifications,
	})),
	on(selectLayoutType, (state, { layoutType }) => ({
		...state,
		selectedLayoutType: layoutType,
	})),
	on(loadedNsFieldsKeyValues, (state, { nsFieldsKeyValues, nameDescriptionList }) => ({
		...state,
		nsFieldsData: {
			nsFieldsKeyValues,
			nameDescriptionList,
		},
	})),
	on(getLayoutOptions, (state, { disableCalendar }) => ({
		...state,
		layoutOptions: CMMS_LAYOUT_OPTIONS(disableCalendar, true), //remove 'true' when list view is ready for Views.
	}))
);

interface ICmmsStateReducer {
	cmmsState: ICmmsState;
	workOrderState: IWorkOrderState;
	requestState: IRequestState;
	incidentState: IIncidentState;
	fileState: IFileState;
	assetState: IIfcAssetsState;
	messagesState: IMessagesState;
	contactsState: IContacts;
}

export const cmmsReducers: ActionReducerMap<ICmmsStateReducer> = {
	cmmsState: cmmsReducer,
	workOrderState: workOrderReducer,
	requestState: requestReducer,
	incidentState: incidentReducer,
	fileState: fileReducer,
	assetState: assetReducer,
	messagesState: messagesReducer,
	contactsState: contactsReducer,
};
